import React, { useEffect, useState } from 'react';
import { Input, Button, Modal, Select, Table, Card, Row, Col, Space, Typography } from 'antd';
import { SearchOutlined, EyeOutlined, UserOutlined, PhoneOutlined, CalendarOutlined, TagOutlined, ClockCircleOutlined, EnvironmentOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import { hosturl } from 'constants/constant';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';

function Tokens() {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [open, setOpen] = useState(false);
  const [filteredTokens, setFilteredTokens] = useState([]);
  const [statusFilter, setStatusFilter] = useState('waiting');
  const user = JSON.parse(localStorage.getItem("user")); // Get the user from localStorage
  const userRole = user ? user.role : null;
  useEffect(() => {
    const fetchTokens = async () => {
      try {
        const response = await axios.get(`${hosturl}/tokens`);
        if (response.data) {
          setTokens(response.data);
          const initialTokens = response.data.filter(
            (token) => token.status === 'waiting' || token.status === 'notresponded'
          );
          setFilteredTokens(initialTokens);
        }
      } catch (err) {
        setError('Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchTokens();
  }, []);

  const handleOpenModal = (user) => {
    setSelectedUser(user);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const filterTokens = () => {
    return filteredTokens.filter((user) => {
      const name = user.name ? user.name.toLowerCase() : '';
      const phoneNumber = user.phoneNumber ? user.phoneNumber : '';
      return (
        name.includes(searchQuery.toLowerCase()) || phoneNumber.includes(searchQuery)
      );
    });
  };

  const handleStatusChange = async (value, tokenNo) => {
    Modal.confirm({
      title: `Are you sure you want to change the status to ${value}?`,
      onOk: async () => {
        try {
          let endpoint;
    
          if (value === 'completed') {
            endpoint = `/token-completed/${tokenNo}`;
          } else if (value === 'notresponded') {
            endpoint = `/token-notresponded/${tokenNo}`;
          } else if (value === 'cancelled') {
            endpoint = `/token-cancelled/${tokenNo}`;
          }
    
          const response = await axios.post(`${hosturl}${endpoint}`, { status: value });
  
          window.location.reload();
  
        } catch (error) {
          alert("Error updating status: " + (error.response?.data || error.message));
        }
      },
    });
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    if (status === 'completed') {
      setFilteredTokens(tokens.filter((token) => token.status === 'completed'));
    } else if (status === 'cancelled') {
      setFilteredTokens(tokens.filter((token) => token.status === 'cancelled'));
    } else {
      setFilteredTokens(tokens.filter(
        (token) => token.status === 'waiting' || token.status === 'notresponded'
      ));
    }
  };
  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID':
        return 'green';
      case 'NOT PAID':
        return 'red';
      case 'PENDING':
        return 'yellow';
      default:
        return '#555'; // Default color for undefined statuses
    }
  };
  const getDaysCompleted = (paymentDate) => {
    const today = moment();
    const paymentMoment = moment(paymentDate); // Convert payment date to moment object
    return today.diff(paymentMoment, 'days'); // Returns the difference in days
  };
   

  const columns = [
    {
      title: 'Queue No',
      dataIndex: 'queueNo',
      key: 'queueNo',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
    },
   {
  title: 'Status',
  dataIndex: 'status',
  key: 'status',
  render: (text, record) => (
    <Select
      value={text}
      style={{ width: 120 }}
      onChange={(value) => handleStatusChange(value, record.tokenNo)}
    >
      <Select.Option value="completed">Completed</Select.Option>
      <Select.Option value="notresponded">Not Responded</Select.Option>
      <Select.Option value="cancelled">Cancelled</Select.Option>
    </Select>
  ),
}
,
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => handleOpenModal(record)}
        >
          View More
        </Button>
      ),
    },
  ];

  const isMobile = window.innerWidth <= 768;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div style={{ padding: 24 }}>
        {/* Header */}
        <Row gutter={16}>
          <Col span={24}>
            <Typography.Title level={3}></Typography.Title>
          </Col>
        </Row>

        {/* Status Filter Buttons */}
        <Row gutter={16} style={{ marginBottom: 16, justifyContent: 'flex-end' }}>
          <Col>
            <Space>
              <Button onClick={() => handleStatusFilter('waiting')} style={{ borderRadius: '8px' }}>Waiting</Button>
              <Button onClick={() => handleStatusFilter('completed')} style={{ borderRadius: '8px' }}>Completed</Button>
              <Button onClick={() => handleStatusFilter('cancelled')} style={{ borderRadius: '8px' }}>Cancelled</Button>
            </Space>
          </Col>
        </Row>

        {/* Search Bar */}
        <Row gutter={16}>
          <Col span={24}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Input
                placeholder="Search by Phone, Name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                prefix={<SearchOutlined />}
                style={{ borderRadius: '8px', padding: '8px 12px', width: '100%' }}
              />
            </Space>
          </Col>
        </Row>

        {/* Display Tokens as Cards on Mobile */}
        {loading ? (
          <Typography.Text>Loading...</Typography.Text>
        ) : error ? (
          <Typography.Text type="danger">{error}</Typography.Text>
        ) : filteredTokens.length === 0 ? (
          <Typography.Text>No data available.</Typography.Text>
        ) : (
          isMobile ? (
            <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
              {filterTokens().map((token) => (
                <Col span={24} sm={12} md={8} lg={6} key={token.tokenNo}>
                  <Card
                    title={`Queue No: ${token.queueNo}`}
                    bordered={false}
                    actions={[
                      <Button
                        type="primary"
                        icon={<EyeOutlined />}
                        onClick={() => handleOpenModal(token)}
                      >
                        View More
                      </Button>
                    ]}
                  >
                    <Typography.Text strong>Name:</Typography.Text> {token.name}
                    <br />
                    <Typography.Text strong>Phone Number:</Typography.Text> {token.phoneNumber}
                    <br />
                    <Typography.Text strong>Status:</Typography.Text> {token.status}
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <Table
              columns={columns}
              dataSource={filterTokens().sort((a, b) => a.queueNo - b.queueNo)} // Sort by queue number
              rowKey="tokenNo"
              pagination={false}
              style={{ marginTop: 24 }}
            />
          )
        )}

        {/* Modal (Dialog) for viewing User Details */}
        <Modal
  visible={open}
  onCancel={handleCloseModal}
  footer={null}
  title="User Details"
  width={600}
  style={{
    borderRadius: '10px',
  }}
  bodyStyle={{
    backgroundColor: '#f9f9f9',
    borderRadius: '10px',
    padding: '24px',
  }}
>
  {selectedUser ? (
    <Card
      bordered={false}
      style={{
        background: '#fff',
        padding: '24px',
        borderRadius: '12px',
      }}
    >
      {/* User Information Section */}
      <Row gutter={24} style={{ marginBottom: '16px' }}>
        <Col xs={24} sm={12} style={{ marginBottom: isMobile ? '8px' : '0' }}>
          <Typography.Text strong style={{ fontSize: '14px', color: '#4a4a4a' }}>
            <UserOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            Name:
          </Typography.Text>
          <Typography.Text style={{ color: '#555', marginLeft: '5px' }}>
            {selectedUser.name}
          </Typography.Text>
        </Col>
        <Col xs={24} sm={12} style={{ marginBottom: isMobile ? '8px' : '0' }}>
          <Typography.Text strong style={{ fontSize: '14px', color: '#4a4a4a' }}>
            <PhoneOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            Phone:
          </Typography.Text>
          <Typography.Text style={{ color: '#555', marginLeft: '5px' }}>
            {selectedUser.phoneNumber}
          </Typography.Text>
        </Col>
      </Row>

      <Row gutter={24} style={{ marginBottom: '16px' }}>
        <Col xs={24} sm={12} style={{ marginBottom: isMobile ? '8px' : '0' }}>
          <Typography.Text strong style={{ fontSize: '14px', color: '#4a4a4a' }}>
            <CalendarOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            DOB:
          </Typography.Text>
          <Typography.Text style={{ color: '#555', marginLeft: '5px' }}>
            {moment(selectedUser.dob).format('YYYY-MM-DD')}
          </Typography.Text>
        </Col>
        <Col xs={24} sm={12} style={{ marginBottom: isMobile ? '8px' : '0' }}>
          <Typography.Text strong style={{ fontSize: '14px', color: '#4a4a4a' }}>
            <TagOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            Zodiac:
          </Typography.Text>
          <Typography.Text style={{ color: '#555', marginLeft: '5px' }}>
            {selectedUser.zodiac}
          </Typography.Text>
        </Col>
      </Row>

      <Row gutter={24} style={{ marginBottom: '16px' }}>
        <Col xs={24} sm={12} style={{ marginBottom: isMobile ? '8px' : '0' }}>
          <Typography.Text strong style={{ fontSize: '14px', color: '#4a4a4a' }}>
            <ClockCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            Time:
          </Typography.Text>
          <Typography.Text style={{ color: '#555' }}>
            {moment(selectedUser.time).format('HH:mm')}
          </Typography.Text>
        </Col>
        <Col xs={24} sm={12} style={{ marginBottom: isMobile ? '8px' : '0' }}>
          <Typography.Text strong style={{ fontSize: '14px', color: '#4a4a4a' }}>
            <EnvironmentOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            Place:
          </Typography.Text>
          <Typography.Text style={{ color: '#555' }}>
            {selectedUser.place}
          </Typography.Text>
        </Col>
      </Row>

      {/* Payment Details Section */}
      {selectedUser.payment && (
        <div>
          <Typography.Title level={5} style={{ marginTop: '24px' }}>
            Payment Details
          </Typography.Title>

          <Row gutter={24} style={{ marginTop: '16px', borderTop: '1px solid #f0f0f0', paddingTop: '16px',gap:'10px' }}>
            <Col xs={24}  style={{ marginBottom: isMobile ? '8px' : '0' }}>
              <Typography.Text strong style={{ fontSize: '14px', color: '#4a4a4a' }}>
                <TagOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                Order Id:
              </Typography.Text>
              <Typography.Text style={{ color: '#555', marginLeft: '5px' }}>
                {selectedUser.payment.orderId}
              </Typography.Text>
            </Col>
            <Col xs={24} sm={12} style={{ marginBottom: isMobile ? '8px' : '0' }}>
              <Typography.Text strong style={{ fontSize: '14px', color: '#4a4a4a' }}>
                <TagOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                Amount:
              </Typography.Text>
              <Typography.Text style={{ color: '#555', marginLeft: '5px' }}>
                {selectedUser.payment.amount}
              </Typography.Text>
            </Col>
            <Col xs={24} sm={12} style={{ marginBottom: isMobile ? '8px' : '0' }}>
              <Typography.Text strong style={{ fontSize: '14px', color: '#4a4a4a' }}>
                <ClockCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                Date:
              </Typography.Text>
              <Typography.Text style={{ color: '#555', marginLeft: '5px' }}>
                {moment(selectedUser.payment.date).format('YYYY-MM-DD')}
              </Typography.Text>
            </Col>
            <Col xs={24} sm={12} style={{ marginBottom: isMobile ? '8px' : '0' }}>
        <Typography.Text strong style={{ fontSize: '14px', color: '#4a4a4a' }}>
          <TagOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
          Status:
        </Typography.Text>
        <Typography.Text
          style={{
            color: getStatusColor(selectedUser.payment.status),
            marginLeft: '5px',
          }}
        >
          {selectedUser.payment.status}
        </Typography.Text>
      </Col>
      <Col xs={24}>
                <Typography.Text strong style={{ fontSize: '14px', color: '#4a4a4a' }}>
                  <ClockCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                  Days Completed Till Now:
                </Typography.Text>
                <Typography.Text style={{ color: '#555', marginLeft: '5px' }}>
                  {getDaysCompleted(selectedUser.payment.date)} days
                </Typography.Text>
              </Col>
          </Row>
        </div>
      )}

      <Space style={{ marginTop: 24 }} align="end">
        <Button type="primary" onClick={handleCloseModal} style={{ borderRadius: '8px' }}>
          Close
        </Button>
      </Space>
    </Card>
  ) : (
    <Typography.Text>No details available.</Typography.Text>
  )}
</Modal>



      </div>
    </DashboardLayout>
  );
}

export default Tokens;
